import { Model, Question } from 'survey-core';
import { LeadCaptureWidgetSubmission } from '../interfaces/lead-capture-submission.interface';

export const TENANT_FIELD_NAMES = [
  'tenant_uuid',
  'clt_tenant',
  'select_tenant',
  'clt_tenant_uuid',
] as const;

const ITEM_CHOICE_PATTERN = /^Item \d+$/i;

export function isEmptySubmissionValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0 || value.every((item) => isEmptySubmissionValue(item));
  }

  return false;
}

export function resolveTenantUuid(formData: Record<string, unknown>): string {
  for (const fieldName of TENANT_FIELD_NAMES) {
    const tenantUuid = formData[fieldName];
    if (typeof tenantUuid === 'string' && tenantUuid) {
      return tenantUuid;
    }
  }

  return '';
}

/** Ensures dial codes are sent with a leading + when present. */
export function normalizeMobileCountryCode(
  value: unknown,
): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  const code = String(value).trim();
  if (!code) {
    return undefined;
  }

  return code.startsWith('+') ? code : `+${code}`;
}

function getSurveyQuestion(
  survey: Model | undefined,
  questionName: string,
): Question | undefined {
  if (!survey || typeof survey.getQuestionByName !== 'function') {
    return undefined;
  }

  return survey.getQuestionByName(questionName);
}

function getChoiceItemValue(choice: unknown): unknown {
  if (typeof choice === 'string' || typeof choice === 'number') {
    return choice;
  }

  if (choice && typeof choice === 'object' && 'value' in choice) {
    return (choice as { value: unknown }).value;
  }

  return undefined;
}

function getChoiceItemText(choice: unknown): unknown {
  if (typeof choice === 'string' || typeof choice === 'number') {
    return choice;
  }

  if (choice && typeof choice === 'object' && 'text' in choice) {
    return (choice as { text: unknown }).text;
  }

  return undefined;
}

function resolveChoiceFromItemPlaceholder(
  question: Question | null | undefined,
  rawValue: string,
): unknown {
  const selectedItem = question?.['selectedItem'];
  if (selectedItem) {
    const selectedValue = getChoiceItemValue(selectedItem);
    const selectedText = getChoiceItemText(selectedItem);

    if (
      selectedValue !== undefined &&
      selectedValue !== null &&
      selectedValue !== '' &&
      !ITEM_CHOICE_PATTERN.test(String(selectedValue))
    ) {
      return selectedValue;
    }

    if (selectedText !== undefined && selectedText !== null && selectedText !== '') {
      return selectedText;
    }
  }

  const choices = question?.['choices'] ?? question?.['visibleChoices'] ?? [];
  const index = Number.parseInt(rawValue.replace(/\D/g, ''), 10) - 1;
  const choice = choices[index];

  if (!choice) {
    return undefined;
  }

  const choiceValue = getChoiceItemValue(choice);
  const choiceText = getChoiceItemText(choice);

  if (
    choiceValue !== undefined &&
    choiceValue !== null &&
    choiceValue !== '' &&
    !ITEM_CHOICE_PATTERN.test(String(choiceValue))
  ) {
    return choiceValue;
  }

  if (choiceText !== undefined && choiceText !== null && choiceText !== '') {
    return choiceText;
  }

  return choiceValue;
}

/** Resolve Survey "Item N" placeholders to the configured choice value/text. */
export function resolveSurveyChoiceValue(
  question: Question | null | undefined,
  rawValue: unknown,
): unknown {
  if (isEmptySubmissionValue(rawValue)) {
    return undefined;
  }

  if (Array.isArray(rawValue)) {
    const resolved = rawValue
      .map((item) => resolveSurveyChoiceValue(question, item))
      .filter((item) => !isEmptySubmissionValue(item));

    return resolved.length > 0 ? resolved : undefined;
  }

  if (typeof rawValue !== 'string') {
    return rawValue;
  }

  if (!ITEM_CHOICE_PATTERN.test(rawValue)) {
    return rawValue;
  }

  return resolveChoiceFromItemPlaceholder(question, rawValue);
}

function resolveSurveyFieldValue(
  survey: Model | undefined,
  questionName: string,
  rawValue: unknown,
): unknown {
  const question = getSurveyQuestion(survey, questionName);
  const resolved = resolveSurveyChoiceValue(question, rawValue);

  if (Array.isArray(resolved) && resolved.length === 1) {
    return resolved[0];
  }

  return resolved;
}

export function mapSurveyDataToLeadCapturePayload(
  formData: Record<string, unknown>,
  defaultTenantUuid = '',
  survey?: Model,
): LeadCaptureWidgetSubmission {
  const tenantUuid = resolveTenantUuid(formData) || defaultTenantUuid || '';
  const cltMobile = formData['clt_mobile1'];
  const genderRaw = String(formData['clt_gender'] ?? formData['gender'] ?? '');

  return {
    widget_type: 'LeadCapture',
    data: {
      tenant_uuid: tenantUuid,
      full_name: formData['clt_name'],
      email_id: formData['clt_email'],
      mobile_country_code: normalizeMobileCountryCode(
        formData['mobile_country_code'],
      ),
      mobile_number:
        typeof cltMobile === 'string' || typeof cltMobile === 'number'
          ? parseInt(String(cltMobile), 10)
          : undefined,
      gender: genderRaw ? mapGender(genderRaw) : undefined,
      location: formData['location'],
      medical_alert: formData['medical_alert'],
      note: formData['clt_notes'] ?? formData['tour_notes'],
      national_id: formData['national_id'],
      client_communication_concent: (formData['client_communication_concent'] as string[] | undefined)?.[0] ===
      'Item 1',
      terms_accepted: (formData['client_tos_concent'] as string[] | undefined)?.[0] === 'Item 1',

      dob: formData['dob'],
      clt_source: resolveSurveyFieldValue(
        survey,
        'clt_source',
        formData['clt_source'],
      ),
      clt_channel: resolveSurveyFieldValue(
        survey,
        'clt_channel',
        formData['clt_channel'],
      ),
      clt_rep: resolveSurveyFieldValue(survey, 'clt_rep', formData['clt_rep']),
    },
    book_tour: '',
    trial_membership: '',
  };
}

/** Allow a fresh submission on every visit; SurveyJS otherwise persists completion in cookies. */
export function configureSurveyForRepeatSubmission(survey: Model): void {
  if (survey.cookieName) {
    survey.deleteCookie();
  }

  survey.cookieName = '';
}

export function applyTourBookingSurveyTweaks(survey: Model): void {
  const tourDateQuestionNames = [
    'clt_tour_datetime',
    'preferred_tour_date',
    'tour_date_time',
    'book_tour_datetime',
    'clt_trial_datetime',
    'preferred_trial_date',
    'trial_date_time',
    'book_trial_datetime',
  ];

  for (const name of tourDateQuestionNames) {
    const question = survey.getQuestionByName(name);
    if (question) {
      question.visible = false;
    }
  }

  survey.getAllQuestions().forEach((question) => {
    const title = question.title?.toString().toLowerCase() ?? '';
    const name = question.name?.toLowerCase() ?? '';
    const isTourScheduleField =
      (title.includes('tour') && (title.includes('date') || title.includes('time'))) ||
      (name.includes('tour') && (name.includes('date') || name.includes('time')));
    const isTrialScheduleField =
      (title.includes('trial') && (title.includes('date') || title.includes('time'))) ||
      (name.includes('trial') && (name.includes('date') || name.includes('time')));

    if (isTourScheduleField || isTrialScheduleField) {
      question.visible = false;
    }
  });
}

function mapGender(gender: string): string {
  switch (gender) {
    case '0':
      return 'Male';
    case '1':
      return 'Female';
    case '2':
      return 'Other';
    default:
      return gender;
  }
}
